import { Button, useToast } from '@nexo/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { CircleCheck, CircleX, Mail } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { AdminPage, QueryState } from './AdminLayout';
import { errorMessage } from '../../lib/forms';

const uptime = (seconds: number, t: TFunction) => {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d
    ? t('{{d}} d {{h}} h', { d, h })
    : h
      ? t('{{h}} h {{m}} min', { h, m })
      : t('{{m}} min', { m });
};

export function AdminSystem() {
  const { t } = useTranslation();
  const toast = useToast();
  const system = useQuery({
    queryKey: ['admin', 'system'],
    queryFn: api.admin.system,
    refetchInterval: 30_000,
  });
  const testEmail = useMutation({
    mutationFn: api.admin.testEmail,
    onSuccess: ({ to }) =>
      toast({
        title: t('Email de prueba enviado'),
        description: t('Revisa {{email}}', { email: to }),
        tone: 'success',
      }),
    onError: (err) =>
      toast({ title: t('El envío ha fallado'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <AdminPage
      title={t('Sistema')}
      description={t('Estado de los servicios de los que depende la plataforma.')}
      actions={
        <Button
          variant="secondary"
          icon={<Mail className="size-4" />}
          loading={testEmail.isPending}
          onClick={() => testEmail.mutate()}
        >
          {t('Enviarme un email de prueba')}
        </Button>
      }
    >
      {!system.data ? (
        <QueryState error={system.error} onRetry={() => void system.refetch()} />
      ) : (
        <>
          <div className="mb-5 flex flex-wrap gap-6 text-sm text-muted">
            <span>
              <Trans
                i18nKey="Versión <strong>{{version}}</strong>"
                values={{ version: system.data.version }}
                components={{ strong: <strong className="text-text" /> }}
              />
            </span>
            <span>
              <Trans
                i18nKey="Entorno <strong>{{environment}}</strong>"
                values={{ environment: system.data.environment }}
                components={{ strong: <strong className="text-text" /> }}
              />
            </span>
            <span>
              <Trans
                i18nKey="En marcha desde hace <strong>{{uptime}}</strong>"
                values={{ uptime: uptime(system.data.uptimeSeconds, t) }}
                components={{ strong: <strong className="text-text" /> }}
              />
            </span>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {system.data.checks.map((check) => (
              <li
                key={check.name}
                className="flex items-start gap-3 rounded-xl border border-border p-4"
              >
                {check.ok ? (
                  <CircleCheck className="size-5 shrink-0 text-success" />
                ) : (
                  <CircleX className="size-5 shrink-0 text-danger" />
                )}
                <div className="min-w-0">
                  <p className="font-medium">
                    {check.name}{' '}
                    <span
                      className={`text-xs font-normal ${check.ok ? 'text-success' : 'text-danger'}`}
                    >
                      {check.ok ? t('Operativo') : t('Revisar')}
                    </span>
                  </p>
                  <p className="text-sm break-words text-muted">{check.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </AdminPage>
  );
}
